import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getRequest, postRequest } from "../utils/axiosClient";
import Constants from "../Constants";
import { initializeSocket } from "../services/SocketService";
import AuthService from "../services/AuthService";
import usePolling from "../hooks/usePolling";

const NOTIFICATIONS_POLL_INTERVAL_MS = 60 * 1000;

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
    // ensure the socket exists synchronously before the useSocket call below tries to subscribe.
    // ProtectedRoute also calls this, but inside a useEffect that fires AFTER child effects,
    // so notification:upserted would race and silently no-op without this line.
    initializeSocket();
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUnread, setTotalUnread] = useState(0);
    const lastFetchParams = useRef({ page: 1, limit: 10 });

    const getNotifications = useCallback(async ({ page = lastFetchParams.current.page, limit = lastFetchParams.current.limit } = {}) => {
        lastFetchParams.current = { page, limit };
        setIsLoading(true);
        try {
            const response = await getRequest({
                url: Constants.ApiUrl.user.getNotifications,
                config: { params: { page, limit } }
            });
            if (response?.data?.success) {
                const data = response.data.data;
                setNotifications(data.items);
                setTotalItems(data.totalItems);
                setTotalPages(data.totalPages);
                setTotalUnread(data.totalUnread);
            }
        } catch (err) {
            throw new Error(err.message, { cause: err });
        } finally {
            setIsLoading(false);
        }
    }, []);

    const markAsRead = useCallback(async ({ caseId, notificationId }) => {
        try {
            const response = await postRequest({
                url: `${Constants.ApiUrl.case.baseCaseUrl}/${caseId}/notifications/${notificationId}/read`,
                data: {}
            });
            if (response?.data?.success) {
                await getNotifications();
            }
        } catch (err) {
            throw new Error(err.message, { cause: err });
        }
    }, [getNotifications]);

    const markAllAsRead = useCallback(async ({ caseId, notificationType }) => {
        try {
            const response = await postRequest({
                url: `${Constants.ApiUrl.case.baseCaseUrl}/${caseId}/notifications/read`,
                data: {
                    notificationType
                }
            });
            if (response?.data?.success) {
                await getNotifications();
            }
        } catch (err) {
            throw new Error(err.message, { cause: err });
        }
    }, [getNotifications]);

    const markCaseAsRead = useCallback((caseId, notificationType) => {
        const hasUnread = notifications.some(n => n.caseId === caseId && !n.isRead);
        if (hasUnread) markAllAsRead({ caseId, notificationType }).catch(err => console.error(err));
    }, [notifications, markAllAsRead]);

    const isLoggedIn = Boolean(AuthService.GetLoggedInUserData());
    usePolling({
        intervalMs: NOTIFICATIONS_POLL_INTERVAL_MS,
        callback: getNotifications,
        enabled: isLoggedIn
    });

    const unreadCaseIds = useMemo(
        () => new Set(notifications.filter(n => !n.isRead).map(n => n.caseId)),
        [notifications]
    );

    const unreadCount = totalUnread;

    const toggleOpen = useCallback(() => {
        setIsOpen((prev) => {
            if (!prev) getNotifications().catch((err) => console.error(err));
            return !prev;
        });
    }, [getNotifications]);
    const closeDropdown = useCallback(() => { if (!isLoading) setIsOpen(false); }, [isLoading]);

    useEffect(() => {
        console.log("effect call");
        getNotifications().catch(err => console.error(err));
    }, [getNotifications]);

    return (
        <NotificationContext.Provider value={{
            notifications, isLoading, getNotifications,
            markAsRead, markAllAsRead, markCaseAsRead,
            unreadCaseIds, unreadCount,
            totalItems, totalPages,
            isOpen, toggleOpen, closeDropdown
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotificationContext = () => useContext(NotificationContext);

export default NotificationContext;
